import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

interface ToggleRowProps {
  title: string
  /** Texto explicativo opcional debajo del título. Omitir si el título
   *  ya transmite qué hace el toggle. */
  description?: string
  checked: boolean
  onToggle: () => void
  /** Icono opcional en un chip amarillo a la izquierda. */
  icon?: ReactNode
  disabled?: boolean
}

/**
 * Fila con (icono) + título (+ descripción opcional) + switch brutalist.
 * Click en la fila entera dispara el toggle (afordancia móvil) además
 * del switch. Estado expuesto con `aria-pressed`.
 *
 * <p>Usado por las preferencias de email del perfil y la capa "Configurar"
 * del banner de cookies.</p>
 */
export function ToggleRow({
  icon,
  title,
  description,
  checked,
  onToggle,
  disabled,
}: ToggleRowProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={checked}
      className={cn(
        'brutal-sm bg-cream-50 flex w-full cursor-pointer items-start gap-3 rounded-xl p-3 text-left transition-colors',
        'hover:bg-yellow/20 disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="chip mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-yellow"
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-bold leading-tight">{title}</p>
        {description && (
          <p className="mt-1 font-brutal text-[11px] leading-snug text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      <span
        aria-hidden="true"
        className={cn(
          'brutal-sm relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-green' : 'bg-red',
        )}
      >
        <span
          className={cn(
            'inline-block size-4 rounded-full bg-background shadow transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </span>
    </button>
  )
}
